import * as React from 'react';

import { useTranslation } from 'react-i18next';

import gtmTracker from '@utils/gtmTracker';

import { COIN_RACE_STATUS, COIN_RACE_ORDER, COIN_RACE_TYPE, CoinRaceFiltersState, TabsType } from '@types';

import { Tabs } from '@components/tabs';

import { config } from '@/config.ts';

import * as Styled from './coin-race.styled';
import { Info } from './components/info';
import { Races } from './components/races';
import { RaceFilters } from './components/races/race-filters';

const CoinRacePage = () => {
	const brand = config.BRAND;
	const { t } = useTranslation('coin_race');
	const isXPBrand = brand === 'com2' || brand === 'sk2';
	const point = isXPBrand ? 'XP' : 'PM';

	const [filtersState, setFiltersState] = React.useState<CoinRaceFiltersState>({
		status: COIN_RACE_STATUS.ACTIVE,
		selectedType: undefined,
		sortOrder: COIN_RACE_ORDER.ASC
	});

	const tabs: TabsType[] = React.useMemo(
		() => [
			{ id: COIN_RACE_STATUS.ACTIVE, name: 'in_progress_p' },
			{ id: COIN_RACE_STATUS.PENDING, name: 'starting_soon' },
			{ id: COIN_RACE_STATUS.COMPLETED, name: 'finished_p' }
		],
		[]
	);

	const handleTrackEvent = (label: string) => {
		gtmTracker.trackEvent('click', {
			event_category: 'Coin_race',
			event_action: 'Click',
			event_label: label
		});
	};

	const handleStatusChange = React.useCallback((id: string) => {
		handleTrackEvent(id);
		setFiltersState(prev => ({
			...prev,
			status: id as COIN_RACE_STATUS
		}));
	}, []);

	const handleTypeChange = React.useCallback((type?: COIN_RACE_TYPE) => {
		type && handleTrackEvent(type);
		setFiltersState(prev => ({
			...prev,
			selectedType: type
		}));
	}, []);

	const handleSortChange = React.useCallback((order: COIN_RACE_ORDER) => {
		handleTrackEvent(order);
		setFiltersState(prev => ({
			...prev,
			sortOrder: order
		}));
	}, []);

	return (
		<Styled.Container>
			<Styled.Title>{t('title')}</Styled.Title>

			<Styled.Description>{t('description', { points: `${point} ${t('points_p')}` })}</Styled.Description>

			<Info />

			<Styled.TabsWrapper>
				<Tabs tabList={tabs} activeTab={filtersState.status} setActiveTab={handleStatusChange} isGray />
			</Styled.TabsWrapper>

			<RaceFilters
				selectedType={filtersState.selectedType}
				sortOrder={filtersState.sortOrder}
				onTypeChange={handleTypeChange}
				onSortChange={handleSortChange}
			/>

			<Races filtersState={filtersState} />
		</Styled.Container>
	);
};

export default CoinRacePage;
